// @ts-ignore
import { Behavior } from '@dia-ui/abstract';
import domElementPath from 'dom-element-path';
import { basicSetup, EditorView } from "codemirror";
import { keymap } from "@codemirror/view";
import { EditorState, Compartment } from '@codemirror/state';
import { json } from '@codemirror/lang-json';
import { LanguageSupport } from '@codemirror/language';
import { javascript } from '@codemirror/lang-javascript';
import { defaultKeymap, indentWithTab } from "@codemirror/commands";
import { basicLight } from "cm6-theme-basic-light";

@Behavior('code-mirror')
export class CodeMirrorBehavior {
  element: HTMLTextAreaElement;

  /**
   * static vars
   */
  static editorView: EditorView[] = [];
  static startState: EditorState[] = [];

  /**
   * constructor
   * @param element
   */
  constructor(element: HTMLTextAreaElement) {
    this.element = element;
  }

  /**
   * connectedCallback
   */
  async connectedCallback() {
    this.initCodeMirror();
  }

  /**
   * domPathIdentifier
   */
  domPathIdentifier(): string {
    return btoa(window.location.pathname + '_' + domElementPath(this.element));
  }

  /**
   * initCodeMirror
   */
  initCodeMirror() {
    /**
     * get identifier based on location and dom path to element
     * this is unique a dom id is unsafe in case of
     * dynamic content based on templates in single page apps
     */
    const domPathIdentifier= this.domPathIdentifier();

    /**
     * init only if not marked as already loaded
     */
    if (!this.element.hasAttribute('data-code-mirror-loaded')) {
      const initialContent = this.element.value || '',
        language= this.element.getAttribute('data-code-mirror-language') || 'json',
        readonly= this.element.hasAttribute('readonly');

      /**
       * define extensions
       */
      let _language = new Compartment,
        _readonly = new Compartment,
        _editable = new Compartment,
        _extensions = [
          basicSetup,
          basicLight,
          // @ts-ignore
          keymap.of([defaultKeymap, indentWithTab]),
          _language.of(this.getLanguageExtension(language)),
          /**
           * define update listener to sync content with textarea element
           */
          EditorView.updateListener.of((update) => {
            if (update.changes) {
              const docString= update.state.doc.toString(),
                event = new CustomEvent('content-change', {
                  detail: {
                    value: docString
                  },
                });

              this.element.dispatchEvent(event);
              this.element.value = docString;
            }
          }),
        ];

      /**
       * set readonly mode if wanted
       */
      if (readonly) {
        _extensions = [
          _readonly.of(EditorState.readOnly.of(true)),
          _editable.of(EditorView.editable.of(false)),
          EditorView.contentAttributes.of({ tabindex: "0" }),
          ..._extensions
        ];
      }

      /**
       * define state to start editor view
       */
      CodeMirrorBehavior.startState[domPathIdentifier] = EditorState.create({
        doc: initialContent,
        extensions: _extensions
      });

      /**
       *  hide textarea only show editor
       */
      this.element.style.display = "none";

      /**
       * init editor view with state on partent element
       * because textareas cant have children
       */
      CodeMirrorBehavior.editorView[domPathIdentifier] = new EditorView({
        state: CodeMirrorBehavior.startState[domPathIdentifier],
        parent: this.element.parentElement
      });

      /**
       * mark element as loaded
       */
      this.element.setAttribute('data-code-mirror-loaded', 'true');

      /**
       * detect content changes and update state with textContent
       */
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length > 0) {
            CodeMirrorBehavior.editorView[domPathIdentifier].setState(EditorState.create({
              doc: mutation.addedNodes[0].textContent,
              extensions: _extensions
            }));
          }
        });
      });

      /**
       * add observer to textarea element
       */
      observer.observe(this.element, {
        childList: true
      });
    }
  }

  /**
   * getLanguageExtension
   * @param language
   */
  getLanguageExtension(language: string): LanguageSupport {
    switch (language.toLowerCase()) {
      case 'json':
        return json();
      case 'typescript':
        return javascript({ typescript: true });
      case 'javascript':
        return javascript();
      default:
        return json();
    }
  }

  /**
   * we wait and listen for element to be defined
   */
  static awaitElementDefined = true;
}
